import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Loader2, Save, Trash2, Plus, FileWarning, X, Type, Calendar, Pen, CheckSquare, Hash, Mail } from "lucide-react";
import type { DocumentTemplate, TemplateField, FieldType } from "@shared/schema";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface TemplateEditorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: DocumentTemplate | null;
}

interface PlacedField {
  localId: string; // local identifier for React key
  fieldKey: string;
  label: string;
  fieldType: FieldType;
  pageNumber: number;
  xPct: number;
  yPct: number;
  widthPct: number;
  heightPct: number;
}

const PALETTE: { fieldKey: string; label: string; fieldType: FieldType; icon: any }[] = [
  { fieldKey: "account_name", label: "Account Name", fieldType: "text", icon: Type },
  { fieldKey: "client_name", label: "Client Name", fieldType: "text", icon: Type },
  { fieldKey: "client_email", label: "Client Email", fieldType: "email", icon: Mail },
  { fieldKey: "client_phone", label: "Client Phone", fieldType: "phone", icon: Hash },
  { fieldKey: "client_address", label: "Client Address", fieldType: "text", icon: Type },
  { fieldKey: "event_date", label: "Event Date", fieldType: "date", icon: Calendar },
  { fieldKey: "event_time", label: "Event Time", fieldType: "text", icon: Type },
  { fieldKey: "event_location", label: "Event Location", fieldType: "text", icon: Type },
  { fieldKey: "performer_name", label: "Performer Name", fieldType: "text", icon: Type },
  { fieldKey: "event_rate", label: "Event Rate", fieldType: "number", icon: Hash },
  { fieldKey: "deposit_amount", label: "Deposit Amount", fieldType: "number", icon: Hash },
  { fieldKey: "balance_due", label: "Balance Due", fieldType: "number", icon: Hash },
  { fieldKey: "invoice_number", label: "Invoice Number", fieldType: "text", icon: Type },
  { fieldKey: "today_date", label: "Today's Date", fieldType: "date", icon: Calendar },
  { fieldKey: "signature", label: "Signature", fieldType: "signature", icon: Pen },
  { fieldKey: "initials", label: "Initials", fieldType: "initial", icon: Pen },
  { fieldKey: "date_signed", label: "Date Signed", fieldType: "date_signed", icon: Calendar },
  { fieldKey: "checkbox", label: "Checkbox", fieldType: "checkbox", icon: CheckSquare },
];

function defaultBox(fieldType: FieldType): { widthPct: number; heightPct: number } {
  if (fieldType === "signature") return { widthPct: 0.25, heightPct: 0.05 };
  if (fieldType === "initial") return { widthPct: 0.08, heightPct: 0.04 };
  if (fieldType === "checkbox") return { widthPct: 0.025, heightPct: 0.025 };
  return { widthPct: 0.18, heightPct: 0.025 };
}

export function TemplateEditorDialog({ open, onOpenChange, template }: TemplateEditorDialogProps) {
  const { toast } = useToast();
  const [numPages, setNumPages] = useState<number>(0);
  const [pageWidth, setPageWidth] = useState<number>(800);
  const [pendingPaletteItem, setPendingPaletteItem] = useState<typeof PALETTE[number] | null>(null);
  const [placed, setPlaced] = useState<PlacedField[]>([]);
  const [selectedLocalId, setSelectedLocalId] = useState<string | null>(null);
  const [customLabel, setCustomLabel] = useState("");
  const [pdfError, setPdfError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  const isPdf = template?.fileMime === "application/pdf" || (template?.fileName || "").toLowerCase().endsWith(".pdf");
  const isDocx = (template?.fileName || "").toLowerCase().endsWith(".docx") || template?.fileMime?.includes("wordprocessingml");

  const fileUrl = template ? `/api/document-templates/${template.id}/file` : "";

  const { data: existingFields = [], isLoading: fieldsLoading } = useQuery<TemplateField[]>({
    queryKey: ["/api/document-templates", template?.id, "fields"],
    enabled: open && !!template?.id,
  });

  // Hydrate placed fields from existing positional rows
  useEffect(() => {
    if (!open || fieldsLoading) return;
    const positional = existingFields.filter((f: any) => f.pageNumber != null);
    setPlaced(
      positional.map((f: any, i: number) => ({
        localId: `existing-${f.id}-${i}`,
        fieldKey: f.fieldKey,
        label: f.label,
        fieldType: f.fieldType as FieldType,
        pageNumber: Number(f.pageNumber),
        xPct: Number(f.xPct),
        yPct: Number(f.yPct),
        widthPct: Number(f.widthPct),
        heightPct: Number(f.heightPct),
      }))
    );
  }, [existingFields, fieldsLoading, open]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setPlaced([]);
      setSelectedLocalId(null);
      setPendingPaletteItem(null);
      setNumPages(0);
      setPdfError(null);
    }
  }, [open]);

  // Compute responsive PDF page width
  useEffect(() => {
    if (!open) return;
    const update = () => {
      const w = containerRef.current?.clientWidth ?? 800;
      setPageWidth(Math.max(400, Math.min(900, w - 32)));
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [open]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!template) return;
      const payload = {
        fields: placed.map((p) => ({
          fieldKey: p.fieldKey,
          label: p.label,
          fieldType: p.fieldType,
          pageNumber: p.pageNumber,
          xPct: p.xPct,
          yPct: p.yPct,
          widthPct: p.widthPct,
          heightPct: p.heightPct,
          isRequired: false,
          dataSource: "client_field",
          clientFieldKey: p.fieldKey,
          prefillBehavior: "prefill_editable",
        })),
      };
      return apiRequest("PUT", `/api/document-templates/${template.id}/fields/layout`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/document-templates", template?.id, "fields"] });
      toast({ title: "Layout saved", description: `${placed.length} field${placed.length === 1 ? "" : "s"} placed.` });
    },
    onError: (e: Error) => toast({ title: "Save failed", description: e.message, variant: "destructive" }),
  });

  const handlePageClick = (pageNumber: number, ev: React.MouseEvent<HTMLDivElement>) => {
    if (!pendingPaletteItem) return;
    const el = pageRefs.current.get(pageNumber);
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const xPct = (ev.clientX - rect.left) / rect.width;
    const yPct = (ev.clientY - rect.top) / rect.height;
    const { widthPct, heightPct } = defaultBox(pendingPaletteItem.fieldType);
    const localId = `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setPlaced((prev) => [
      ...prev,
      {
        localId,
        fieldKey: pendingPaletteItem.fieldKey,
        label: pendingPaletteItem.label,
        fieldType: pendingPaletteItem.fieldType,
        pageNumber,
        xPct: Math.max(0, Math.min(1 - widthPct, xPct - widthPct / 2)),
        yPct: Math.max(0, Math.min(1 - heightPct, yPct - heightPct / 2)),
        widthPct,
        heightPct,
      },
    ]);
    setSelectedLocalId(localId);
    setPendingPaletteItem(null);
  };

  // Drag-to-move logic
  const dragRef = useRef<{ localId: string; startX: number; startY: number; startXPct: number; startYPct: number; pageRect: DOMRect } | null>(null);
  const handleFieldMouseDown = (ev: React.MouseEvent, field: PlacedField) => {
    ev.stopPropagation();
    setSelectedLocalId(field.localId);
    const el = pageRefs.current.get(field.pageNumber);
    if (!el) return;
    const pageRect = el.getBoundingClientRect();
    dragRef.current = {
      localId: field.localId,
      startX: ev.clientX,
      startY: ev.clientY,
      startXPct: field.xPct,
      startYPct: field.yPct,
      pageRect,
    };
    const onMove = (e: MouseEvent) => {
      if (!dragRef.current) return;
      const dxPct = (e.clientX - dragRef.current.startX) / dragRef.current.pageRect.width;
      const dyPct = (e.clientY - dragRef.current.startY) / dragRef.current.pageRect.height;
      setPlaced((prev) =>
        prev.map((p) =>
          p.localId === dragRef.current!.localId
            ? {
                ...p,
                xPct: Math.max(0, Math.min(1 - p.widthPct, dragRef.current!.startXPct + dxPct)),
                yPct: Math.max(0, Math.min(1 - p.heightPct, dragRef.current!.startYPct + dyPct)),
              }
            : p
        )
      );
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  // Resize via bottom-right handle
  const resizeRef = useRef<{ localId: string; startX: number; startY: number; startW: number; startH: number; pageRect: DOMRect } | null>(null);
  const handleResizeMouseDown = (ev: React.MouseEvent, field: PlacedField) => {
    ev.stopPropagation();
    const el = pageRefs.current.get(field.pageNumber);
    if (!el) return;
    const pageRect = el.getBoundingClientRect();
    resizeRef.current = {
      localId: field.localId,
      startX: ev.clientX,
      startY: ev.clientY,
      startW: field.widthPct,
      startH: field.heightPct,
      pageRect,
    };
    const onMove = (e: MouseEvent) => {
      if (!resizeRef.current) return;
      const dwPct = (e.clientX - resizeRef.current.startX) / resizeRef.current.pageRect.width;
      const dhPct = (e.clientY - resizeRef.current.startY) / resizeRef.current.pageRect.height;
      setPlaced((prev) =>
        prev.map((p) =>
          p.localId === resizeRef.current!.localId
            ? {
                ...p,
                widthPct: Math.max(0.02, Math.min(1 - p.xPct, resizeRef.current!.startW + dwPct)),
                heightPct: Math.max(0.015, Math.min(1 - p.yPct, resizeRef.current!.startH + dhPct)),
              }
            : p
        )
      );
    };
    const onUp = () => {
      resizeRef.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const removeField = (localId: string) => {
    setPlaced((prev) => prev.filter((p) => p.localId !== localId));
    if (selectedLocalId === localId) setSelectedLocalId(null);
  };

  const addCustomField = () => {
    const label = customLabel.trim();
    if (!label) return;
    const fieldKey = label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
    if (!fieldKey) return;
    setPendingPaletteItem({ fieldKey, label, fieldType: "text", icon: Type });
    setCustomLabel("");
    toast({ title: "Click on the document", description: `Click anywhere on the page to place "${label}"` });
  };

  const placedByPage = useMemo(() => {
    const m = new Map<number, PlacedField[]>();
    for (const p of placed) {
      const arr = m.get(p.pageNumber) || [];
      arr.push(p);
      m.set(p.pageNumber, arr);
    }
    return m;
  }, [placed]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] w-[95vw] h-[92vh] p-0 flex flex-col" data-testid="dialog-template-editor">
        <DialogHeader className="px-6 py-3 border-b shrink-0">
          <DialogTitle className="flex items-center justify-between gap-4">
            <span className="truncate">Edit Template — {template?.name || ""}</span>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" data-testid="badge-placed-count">{placed.length} field{placed.length === 1 ? "" : "s"} placed</Badge>
              <Button
                size="sm"
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending || !template}
                data-testid="button-save-layout"
              >
                {saveMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Layout
              </Button>
            </div>
          </DialogTitle>
          <DialogDescription>
            {pendingPaletteItem
              ? <span className="text-primary font-medium">Click anywhere on the document to place "{pendingPaletteItem.label}". Press Esc to cancel.</span>
              : <span>Choose a merge field on the left, then click on the document to place it. Drag to move, drag the bottom-right corner to resize.</span>}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 grid grid-cols-[280px_1fr] overflow-hidden">
          {/* Palette sidebar */}
          <div className="border-r flex flex-col overflow-hidden">
            <div className="p-3 border-b">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Merge Fields</Label>
              <p className="text-xs text-muted-foreground mt-1">Click a field, then click the document.</p>
            </div>
            <ScrollArea className="flex-1">
              <div className="p-2 space-y-1">
                {PALETTE.map((item) => {
                  const Icon = item.icon;
                  const active = pendingPaletteItem?.fieldKey === item.fieldKey;
                  return (
                    <button
                      key={item.fieldKey}
                      onClick={() => setPendingPaletteItem(active ? null : item)}
                      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm text-left border transition-colors ${active ? "bg-primary/10 border-primary text-primary" : "border-transparent hover:bg-accent hover:text-accent-foreground"}`}
                      data-testid={`palette-${item.fieldKey}`}
                    >
                      <Icon className="w-3.5 h-3.5 shrink-0" />
                      <span className="flex-1 truncate">{item.label}</span>
                      <span className="text-[10px] font-mono text-muted-foreground">{`{{${item.fieldKey}}}`}</span>
                    </button>
                  );
                })}
              </div>
              <div className="p-3 border-t space-y-2">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Custom Field</Label>
                <div className="flex gap-2">
                  <Input
                    value={customLabel}
                    onChange={(e) => setCustomLabel(e.target.value)}
                    placeholder="e.g. Venue Name"
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomField(); } }}
                    data-testid="input-custom-field-label"
                  />
                  <Button size="icon" variant="outline" onClick={addCustomField} disabled={!customLabel.trim()} data-testid="button-add-custom-field">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              {selectedLocalId && (() => {
                const f = placed.find((p) => p.localId === selectedLocalId);
                if (!f) return null;
                return (
                  <div className="p-3 border-t space-y-2 bg-muted/30">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs uppercase tracking-wide text-muted-foreground">Selected</Label>
                      <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => removeField(f.localId)} data-testid="button-delete-selected-field">
                        <Trash2 className="w-3.5 h-3.5 text-destructive" />
                      </Button>
                    </div>
                    <div className="text-sm font-medium">{f.label}</div>
                    <div className="text-xs font-mono text-muted-foreground">{`{{${f.fieldKey}}}`}</div>
                    <div className="space-y-1">
                      <Label className="text-xs">Field Type</Label>
                      <Select
                        value={f.fieldType}
                        onValueChange={(value: FieldType) =>
                          setPlaced((prev) => prev.map((p) => (p.localId === f.localId ? { ...p, fieldType: value } : p)))
                        }
                      >
                        <SelectTrigger className="h-8" data-testid="select-selected-field-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="text">Text</SelectItem>
                          <SelectItem value="number">Number</SelectItem>
                          <SelectItem value="date">Date</SelectItem>
                          <SelectItem value="email">Email</SelectItem>
                          <SelectItem value="phone">Phone</SelectItem>
                          <SelectItem value="signature">Signature</SelectItem>
                          <SelectItem value="initial">Initial</SelectItem>
                          <SelectItem value="date_signed">Date Signed</SelectItem>
                          <SelectItem value="checkbox">Checkbox</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      Page {f.pageNumber} · {(f.xPct * 100).toFixed(1)}%, {(f.yPct * 100).toFixed(1)}% · {(f.widthPct * 100).toFixed(1)}×{(f.heightPct * 100).toFixed(1)}%
                    </div>
                  </div>
                );
              })()}
            </ScrollArea>
          </div>

          {/* Document canvas */}
          <div ref={containerRef} className="overflow-auto bg-muted/40">
            {!template ? (
              <div className="flex items-center justify-center h-full text-muted-foreground">No template selected</div>
            ) : !template.fileObjectKey ? (
              <div className="flex flex-col items-center justify-center h-full text-center p-6">
                <FileWarning className="w-10 h-10 text-muted-foreground mb-2" />
                <p className="font-medium">No file uploaded</p>
                <p className="text-sm text-muted-foreground">Upload a PDF for this template first, then come back to place fields.</p>
              </div>
            ) : isDocx ? (
              <div className="flex flex-col items-center justify-center h-full text-center p-6">
                <FileWarning className="w-10 h-10 text-amber-500 mb-2" />
                <p className="font-medium">DOCX visual placement isn't supported yet</p>
                <p className="text-sm text-muted-foreground max-w-md">
                  Please re-upload this template as a PDF to place fields visually. (DOCX files can still be sent as-is.)
                </p>
              </div>
            ) : isPdf ? (
              <div className="py-4 flex flex-col items-center gap-4" onKeyDown={(e) => { if (e.key === "Escape") setPendingPaletteItem(null); }} tabIndex={0}>
                {pdfError && (
                  <div className="text-destructive text-sm p-4 border border-destructive/30 rounded bg-destructive/5">
                    Failed to load PDF: {pdfError}
                  </div>
                )}
                <Document
                  file={fileUrl}
                  onLoadSuccess={({ numPages }) => { setNumPages(numPages); setPdfError(null); }}
                  onLoadError={(err) => setPdfError(err?.message || "Unknown error")}
                  loading={<div className="p-8 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading PDF…</div>}
                >
                  {Array.from({ length: numPages }, (_, i) => i + 1).map((pageNumber) => {
                    const fieldsHere = placedByPage.get(pageNumber) || [];
                    return (
                      <div key={pageNumber} className="relative shadow-md mb-6 bg-white" data-testid={`pdf-page-${pageNumber}`}>
                        <div
                          ref={(el) => { if (el) pageRefs.current.set(pageNumber, el); else pageRefs.current.delete(pageNumber); }}
                          className="relative"
                          onClick={(e) => handlePageClick(pageNumber, e)}
                          style={{ cursor: pendingPaletteItem ? "crosshair" : "default" }}
                        >
                          <Page pageNumber={pageNumber} width={pageWidth} renderAnnotationLayer={false} renderTextLayer={false} />
                          {/* Overlays */}
                          {fieldsHere.map((f) => {
                            const selected = selectedLocalId === f.localId;
                            return (
                              <div
                                key={f.localId}
                                className={`absolute border-2 ${selected ? "border-primary bg-primary/20" : "border-blue-500 bg-blue-500/15"} rounded-sm flex items-center justify-center text-[10px] font-medium select-none`}
                                style={{
                                  left: `${f.xPct * 100}%`,
                                  top: `${f.yPct * 100}%`,
                                  width: `${f.widthPct * 100}%`,
                                  height: `${f.heightPct * 100}%`,
                                  cursor: "move",
                                }}
                                onMouseDown={(e) => handleFieldMouseDown(e, f)}
                                onClick={(e) => e.stopPropagation()}
                                data-testid={`field-overlay-${f.localId}`}
                              >
                                <span className="px-1 truncate pointer-events-none">{f.label}</span>
                                <button
                                  onClick={(e) => { e.stopPropagation(); removeField(f.localId); }}
                                  className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full w-4 h-4 flex items-center justify-center text-[8px] shadow"
                                  data-testid={`button-remove-overlay-${f.localId}`}
                                >
                                  <X className="w-2.5 h-2.5" />
                                </button>
                                <div
                                  onMouseDown={(e) => handleResizeMouseDown(e, f)}
                                  className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-primary cursor-se-resize"
                                  data-testid={`resize-handle-${f.localId}`}
                                />
                              </div>
                            );
                          })}
                        </div>
                        <div className="text-[10px] text-center text-muted-foreground py-1 border-t">Page {pageNumber} of {numPages}</div>
                      </div>
                    );
                  })}
                </Document>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground">Unsupported file type</div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
